import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux'
import { setMessage } from 'src/store/alertMessage/actions'
import { constants } from 'src/utils/const'
import DatePicker from "react-datepicker";
import { getParty, getCommissionData } from 'src/app/modules/party-entry/auto-vatav/_request';
import {
  AutoVatavFormData,
  fetchReceiverOptionsAPI,
  fetchSenderOptionsAPI,
  PartyOption,
  fetchPartyDetailsAPI,
  fetchCommissionDataAPI,
  submitAutoVatavFormAPI,

} from './_request';
import { KTSVG } from 'src/_metronic/helpers';
import { useNavigate } from 'react-router-dom';

const AutoVatav: React.FC = () => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState<any>(false)
  const today = new Date();
  const navigate = useNavigate();
  const [date] = useState(new Date().toISOString().split('T')[0]);
  const [senderParty, setSenderParty] = useState<PartyOption | null>(null);
  const [receiverParty, setReceiverParty] = useState<PartyOption | null>(null);
  const [senderOptions, setSenderOptions] = useState<PartyOption[]>([]);
  const [receiverOptions, setReceiverOptions] = useState<PartyOption[]>([]);
  const [balances, setBalances] = useState<any>(null);
  const [charCount, setCharCount] = useState(70)

  const [isSenderMenuOpen, setIsSenderMenuOpen] = useState(true);
  const [isReceiverMenuOpen, setIsReceiverMenuOpen] = useState(false);

  const senderSelectRef = useRef<any>(null);
  const receiverSelectRef = useRef<any>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const senderVatavRef = useRef<any>(null);
  const receiverVatavRef = useRef<any>(null);
  const remarksRef = useRef<any>(null);

  const [allParties, setAllParties] = useState<PartyOption[]>([]);

  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
    document.title = `PANEL | Auto Vatav`

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const response = await getParty();
        const parties = response.data.map((x: any) => ({
          label: x.nameCode,
          value: x.partyId,
          ...x,
        }));
        setAllParties(parties);
        setSenderOptions(parties);
        setReceiverOptions(parties);
      } catch (error) {
        console.error('Error fetching parties:', error);
        dispatch(setMessage('error', 'Something went wrong'));
      }
    };
    fetchParties();
    setTimeout(() => {
      senderSelectRef.current?.focus();
      senderSelectRef.current?.onMenuOpen();
    }, 0);
  }, []);

  const formik = useFormik({
    initialValues: {
      senderParty: '',
      receiverParty: '',
      amount: '',
      senderVatav: '',
      receiverVatav: '',
      remarks: '',
    },
    validationSchema: Yup.object({
      senderParty: Yup.string().required('Sender party is required'),
      receiverParty: Yup.string().required('Receiver party is required'),
      amount: Yup.number()
      .typeError('Amount must be a number')
      .required('Amount is required'),
      senderVatav: Yup.number()
        .typeError('Sender vatav must be a number')
        .test('not-zero', 'Sender vatav can\'t be zero', value => value !== 0)
        .required('Sender vatav is required'),
      receiverVatav: Yup.number()
        .typeError('Receiver vatav must be a number')
        .required('Receiver vatav is required')
        .test('not-zero', 'Receiver vatav can\'t be zero', value => value !== 0)
        .test('receiver-vatav', 'Receiver vatav cannot be more than sender vatav', function (value) {
          return typeof value === 'number' && value <= this.parent.senderVatav;
        }),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true)
      const selectedSender = senderOptions.find(s => s.value === values.senderParty)
      const selectedReceiver = receiverOptions.find(r => r.value === values.receiverParty)
      const amount = parseFloat(values.amount || '0');
      const formData = {
        date,
        sender_id: selectedSender?.value,
        receiver_id: selectedReceiver?.value,
        amount: values.amount,
        sender_vatav: values.senderVatav,
        receiver_vatav: values.receiverVatav,
        remarks: values.remarks,
      }
      try {
        const response = await submitAutoVatavFormAPI(formData)
        if (response?.status === 200) {
          dispatch(setMessage('success', 'Entry added successfully!'))
          setCharCount(70)
          formik.setFieldValue('receiverParty', '')
          formik.setFieldValue('amount', '')
          formik.setFieldValue('senderVatav', '')
          formik.setFieldValue('receiverVatav', '')
          formik.setFieldValue('remarks', '')
          setReceiverParty(null)
          setBalances(null)
          setSenderOptions(allParties)
          setReceiverOptions(allParties.filter(option => option.value !== senderParty?.value))
          receiverSelectRef.current?.focus()
          receiverSelectRef.current?.onMenuOpen()

          if (selectedSender && selectedReceiver) {
            try {
              const commissionPayload = {
                sender_id: selectedSender.value,
                receiver_id: '',
                amount: '0',
                vatav: 1,
              };
              const commissionData = await getCommissionData(commissionPayload);
              setBalances(commissionData);
              setSenderParty((prev: any) => ({
                ...prev,
                current_balance: commissionData.sender_current_balance,
                debit: commissionData.sender_debit,
                net_balance: commissionData.sender_net_balance,
              }));
              setReceiverParty((prev: any) => ({
                ...prev,
                current_balance: commissionData.receiver_current_balance,
                credit: commissionData.receiver_credit,
                net_balance: commissionData.receiver_net_balance,
              }));
            } catch (error) {
              console.error('Error fetching commission data after submit:', error);
            }
          }
        } else {
          dispatch(setMessage('error', response.data.message || 'Failed to add transaction!'))
        }
      } catch (error: any) {
        dispatch(setMessage('error', error?.message || 'Something went wrong'))
      } finally {
        setLoading(false)
      }
    },
  });

  const handleSenderPartyChange = async (selected: any) => {
    formik.setFieldValue('senderParty', selected.value);
    receiverSelectRef.current?.onMenuOpen();
    const selectedParty = allParties.find(option => option.value === selected.value) || null;
    if (!selectedParty) return;
    setSenderParty(selectedParty);
    setReceiverOptions(allParties.filter(option => option.value !== selected.value));

    const payload = {
      sender_id: selectedParty.partyId,
      amount: formik.values.amount || '0',
      vatav: 1,
      sender_vatav: formik.values.senderVatav || '0',
      receiver_vatav: formik.values.receiverVatav || '0',
    };

    try {
      const partyDetails = await getCommissionData(payload);
      setSenderParty((prev: any) => ({
        ...prev,
        current_balance: partyDetails.sender_current_balance,
        debit: partyDetails.sender_debit,
        net_balance: partyDetails.sender_net_balance,
      }));

      setBalances({
        sender_current_balance: partyDetails.sender_current_balance,
        sender_debit: partyDetails.sender_debit,
        sender_net_balance: partyDetails.sender_net_balance,
      });

      setTimeout(() => receiverSelectRef.current?.focus(), 0);
    } catch (error) {
      console.error('Error fetching commission data:', error);
    }
  };

  const handleReceiverPartyChange = async (selected: any) => {
    formik.setFieldValue('receiverParty', selected.value);
    setIsReceiverMenuOpen(false);

    const selectedParty = allParties.find(option => option.value === selected.value) || null;
    if (!selectedParty) return;
    setReceiverParty(selectedParty);
    setSenderOptions(allParties.filter(option => option.value !== selected.value));

    if (senderParty) {
      const payload = {
        sender_id: senderParty.partyId,
        receiver_id: selectedParty.partyId,
        amount: formik.values.amount || '0',
        vatav: 1,
        sender_vatav: formik.values.senderVatav || '0',
        receiver_vatav: formik.values.receiverVatav || '0',
      };

      try {
        const partyDetails = await getCommissionData(payload);
        setSenderParty((prev: any) => ({
          ...prev,
          current_balance: partyDetails.sender_current_balance,
          debit: partyDetails.sender_debit,
          net_balance: partyDetails.sender_net_balance,
        }));
        setReceiverParty((prev: any) => ({
          ...prev,
          current_balance: partyDetails.receiver_current_balance,
          credit: partyDetails.receiver_credit,
          net_balance: partyDetails.receiver_net_balance,
        }));
        setBalances({
          sender_current_balance: partyDetails.sender_current_balance,
          sender_debit: partyDetails.sender_debit,
          sender_net_balance: partyDetails.sender_net_balance,
          receiver_current_balance: partyDetails.receiver_current_balance,
          receiver_credit: partyDetails.receiver_credit,
          receiver_net_balance: partyDetails.receiver_net_balance,
        });

        setTimeout(() => amountInputRef.current?.focus(), 0);
      } catch (error) {
        console.error('Error fetching commission data:', error);
      }
    }
  };

  const handleAmountChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (value.length > 12) {
      value = value.slice(0, 12)
    }
    if (/^-?\d*$/.test(value)) {
      formik.setFieldValue('amount', value)
    }

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(async () => {
      if (senderParty && receiverParty) {
        const amount = parseFloat(value || '0');
        const payload = {
          sender_id: senderParty?.value ?? null,
          receiver_id: receiverParty?.value ?? null,
          amount: Math.abs(amount).toString(),
          vatav: 1,
          sender_vatav: formik.values.senderVatav || '0',
          receiver_vatav: formik.values.receiverVatav || '0',
        }
        try {
          const commissionData = await getCommissionData(payload)
          setSenderParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.sender_current_balance,
            debit: commissionData.sender_debit,
            net_balance: commissionData.sender_net_balance,
          }))
          setReceiverParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.receiver_current_balance,
            credit: commissionData.receiver_credit,
            net_balance: commissionData.receiver_net_balance,
          }))
          setBalances(commissionData)
        } catch (error) {
          console.error('Error fetching commission data:', error)
        }
      }
    }, 1000);

    setDebounceTimeout(timeout);
  };

  const handleSenderVatavChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (value.length > 12) {
      value = value.slice(0, 12)
    }
    if (/^\d*$/.test(value)) {
      formik.setFieldValue('senderVatav', value)
    }

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(async () => {
      if (senderParty && receiverParty) {
        const amount = Number(formik.values.amount || '0');
        const senderVatav = Number(value || '0');
        const payload = {
          sender_id: senderParty?.value ?? null,
          receiver_id: receiverParty?.value ?? null,
          amount: Math.abs(amount).toString(),
          vatav: 1,
          sender_vatav: value || '0',
          receiver_vatav: formik.values.receiverVatav || '0',
        }
        try {
          const commissionData = await getCommissionData(payload)
          setSenderParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.sender_current_balance,
            debit: commissionData.sender_debit,
            net_balance: commissionData.sender_net_balance,
          }))
          setReceiverParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.receiver_current_balance,
            credit: commissionData.receiver_credit,
            net_balance: commissionData.receiver_net_balance,
          }))
          setBalances(commissionData)
        } catch (error) {
          console.error('Error fetching commission data:', error)
        }
      }
    }, 1000);

    setDebounceTimeout(timeout);
  };

  const handleReceiverVatavChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (value.length > 12) {
      value = value.slice(0, 12)
    }
    if (/^\d*$/.test(value)) {
      formik.setFieldValue('receiverVatav', value)
    }

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(async () => {
      if (senderParty && receiverParty) {
        const amount = Number(formik.values.amount || '0');
        const receiverVatav = Number(value || '0');
        const payload = {
          sender_id: senderParty?.value ?? null,
          receiver_id: receiverParty?.value ?? null,
          amount: Math.abs(amount).toString(),
          vatav: 1,
          sender_vatav: formik.values.senderVatav || '0',
          receiver_vatav: value || '0',
        }
        try {
          const commissionData = await getCommissionData(payload)
          setSenderParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.sender_current_balance,
            debit: commissionData.sender_debit,
            net_balance: commissionData.sender_net_balance,
          }))
          setReceiverParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.receiver_current_balance,
            credit: commissionData.receiver_credit,
            net_balance: commissionData.receiver_net_balance,
          }))
          setBalances(commissionData)
        } catch (error) {
          console.error('Error fetching commission data:', error)
        }
      }
    }, 1000);

    setDebounceTimeout(timeout);
  };

  const handleCancel = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    formik.setTouched({}, false);
    formik.resetForm();
    setSenderParty(null);
    setReceiverParty(null);
    setBalances(null);
    setCharCount(70);
    setSenderOptions(allParties);
    setReceiverOptions(allParties);
    senderSelectRef.current?.focus();
    senderSelectRef.current?.onMenuOpen();
  };
  
  const preventNonNumeric = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[0-9-]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      e.preventDefault();
    }
  };

  const safeFormatAmount = (val: any) => {
    if (val === undefined || val === null || isNaN(Number(val))) return '-';
    return constants.formatAmount(val);
  };

  const amount = Number(formik.values.amount || '0');
  const senderVatav = Number(formik.values.senderVatav || '0');
  const receiverVatav = Number(formik.values.receiverVatav || '0');
  const absAmount = Math.abs(amount);

  let displaySender = senderParty;
  let displayReceiver = receiverParty;
  let displaySenderLabel = 'Debit';
  let displayReceiverLabel = 'Credit';
  let displaySenderAmount = '';
  let displayReceiverAmount = '';
  let displaySenderNet = '';
  let displayReceiverNet = '';

  if (amount >= 0) {
    displaySenderLabel = 'Debit';
    displayReceiverLabel = 'Credit';
    displaySenderAmount = `+${absAmount + senderVatav}`;
    displayReceiverAmount = `-${absAmount + receiverVatav}`;
    displaySenderNet = safeFormatAmount(Number(senderParty?.current_balance || 0) + absAmount + senderVatav);
    displayReceiverNet = safeFormatAmount(Number(receiverParty?.current_balance || 0) - (absAmount + receiverVatav));
  } else {
    displaySenderLabel = 'Credit';
    displayReceiverLabel = 'Debit';
    displaySenderAmount = `-${absAmount + receiverVatav}`;
    displayReceiverAmount = `+${absAmount + senderVatav}`;
    displaySenderNet = safeFormatAmount(Number(senderParty?.current_balance || 0) - (absAmount + receiverVatav));
    displayReceiverNet = safeFormatAmount(Number(receiverParty?.current_balance || 0) + absAmount + senderVatav);
  }

  const displaySenderBalance = safeFormatAmount(displaySender?.current_balance);
  const displaySenderDebit = safeFormatAmount(displaySenderAmount);
  const displayReceiverBalance = safeFormatAmount(displayReceiver?.current_balance);
  const displayReceiverCredit = safeFormatAmount(displayReceiverAmount);

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: '#E4E6EF',
      borderWidth: '2px',
      minHeight: 'calc(1.5em + .75rem + 16px)',
      borderRadius: '0.475rem',
      cursor: 'pointer',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#cdcdde',
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      lineHeight: '1.5',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: 'calc(1.5em + .75rem + 16px)',
    }),
    option: (provided: any) => ({
      ...provided,
      cursor: 'pointer',
    }),
  };

  return (
    <div className="card">
      <div className="card-header min-h-auto py-5">
        <h2 className="fw-bolder">Auto Vatav</h2>
        <div
          className="btn btn-icon btn-shadow-lg bg-primary text-white position-absolute start-100 translate-middle d-flex align-items-center justify-content-center border border-light"
          style={{ zIndex: 99, top: "33px", marginLeft: "-34px", width: "34px", height: "32px" }}
          role="button"
          aria-label="Go back to previous page"
          tabIndex={0}
          data-tooltip="Back"
          onClick={() => navigate(-1)}
        >
          <KTSVG path="/media/icons/duotune/arrows/arr025.svg" className="svg-icon-4 text-white" />
        </div>
      </div>
      <div className="card-body">
        <Form onSubmit={formik.handleSubmit}>
          <Row className="mb-5">
            <Col md={6}>
              <Form.Group controlId="date">
                <Form.Label>Date</Form.Label>
                <DatePicker
                  selected={today}
                  className="form-control"
                  dateFormat="dd-MM-yyyy"
                  disabled
                />
              </Form.Group>
            </Col>
            <Col md={6}></Col>
          </Row>

          <Row className="mb-5 align-items-center">
            <Col md={6}>
              <Form.Group controlId="senderParty">
                <Form.Label>Sender Party</Form.Label>
                <Select
                  ref={senderSelectRef}
                  id="sender-party"
                  onChange={handleSenderPartyChange}
                  onBlur={() => formik.setFieldTouched('senderParty', true)}
                  options={senderOptions.map((option) => ({
                    value: option.partyId,
                    label: option.nameCode,
                  }))}
                  placeholder="Sender Party"
                  value={
                    senderParty
                      ? { value: senderParty.partyId, label: senderParty.nameCode }
                      : null
                  }
                  styles={customSelectStyles}
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !formik.values.senderParty) {
                      e.preventDefault();
                      receiverSelectRef.current?.focus();
                      receiverSelectRef.current?.onMenuOpen();
                    }
                  }}
                />
                {formik.touched.senderParty && formik.errors.senderParty && (
                  <div className="fv-plugins-message-container text-danger fs-8">
                    <span role="alert">{formik.errors.senderParty}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-8">Current Balance</Form.Label>
              <div>{displaySenderBalance}</div>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-8">{displaySenderLabel}</Form.Label>
              <div>{displaySenderDebit}</div>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-8">Net Balance</Form.Label>
              <div>{displaySenderNet}</div>
            </Col>
          </Row>

          <Row className="mb-5 align-items-center">
            <Col md={6}>
              <Form.Group controlId="receiverParty">
                <Form.Label>Receiver Party</Form.Label>
                <Select
                  ref={receiverSelectRef}
                  id="receiver-party"
                  onChange={handleReceiverPartyChange}
                  onBlur={() => formik.setFieldTouched('receiverParty', true)}
                  options={receiverOptions.map((option) => ({
                    value: option.partyId,
                    label: option.nameCode,
                  }))}
                  placeholder="Receiver Party"
                  styles={customSelectStyles}
                  value={
                    receiverParty
                      ? { value: receiverParty.partyId, label: receiverParty.nameCode }
                      : null
                  }
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !formik.values.receiverParty) {
                      e.preventDefault();
                      amountInputRef.current?.focus();
                    }
                  }}
                />
                {formik.touched.receiverParty && formik.errors.receiverParty && (
                  <div className="fv-plugins-message-container text-danger fs-8">
                    <span role="alert">{formik.errors.receiverParty}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-8">Current Balance</Form.Label>
              <div>{displayReceiverBalance}</div>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-8">{displayReceiverLabel}</Form.Label>
              <div>{displayReceiverCredit}</div>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-8">Net Balance</Form.Label>
              <div>{displayReceiverNet}</div>
            </Col>
          </Row>

          <Row className="mb-5">
            <Col md={4}>
              <Form.Group controlId="amount">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="text"
                  name="amount"
                  inputMode="numeric"
                  pattern="[0-9-]*"
                  maxLength={9}
                  value={formik.values.amount}
                  onChange={handleAmountChange}
                  onBlur={formik.handleBlur}
                  ref={amountInputRef}
                  onKeyPress={preventNonNumeric}
                  placeholder='Amount'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      senderVatavRef.current?.focus();
                    }
                  }}
                />
                {formik.touched.amount && formik.errors.amount && (
                  <div className="fv-plugins-message-container text-danger fs-8">
                    <span role="alert">{formik.errors.amount}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="senderVatav">
                <Form.Label>Sender Vatav</Form.Label>
                <Form.Control
                  type="text"
                  name="senderVatav"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={9}
                  ref={senderVatavRef}
                  value={formik.values.senderVatav}
                  onChange={handleSenderVatavChange}
                  onBlur={formik.handleBlur}
                  onKeyPress={preventNonNumeric}
                  placeholder='Sender Vatav'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      receiverVatavRef.current?.focus();
                    }
                  }}
                />
                {formik.touched.senderVatav && formik.errors.senderVatav && (
                  <div className="fv-plugins-message-container text-danger fs-8">
                    <span role="alert">{formik.errors.senderVatav}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="receiverVatav">
                <Form.Label>Receiver Vatav</Form.Label>
                <Form.Control
                  type="text"
                  name="receiverVatav"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={9}
                  ref={receiverVatavRef}
                  value={formik.values.receiverVatav}
                  onChange={handleReceiverVatavChange}
                  onBlur={formik.handleBlur}
                  onKeyPress={preventNonNumeric}
                  placeholder='Receiver Vatav'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      remarksRef.current?.focus();
                    }
                  }}
                />
                {formik.touched.receiverVatav && formik.errors.receiverVatav && (
                  <div className="fv-plugins-message-container text-danger fs-8">
                    <span role="alert">{formik.errors.receiverVatav}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-5">
            <Col md={12}>
              <Form.Group controlId="remarks">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="remarks"
                  ref={remarksRef}
                  value={formik.values.remarks}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (inputValue.length <= 70) {
                      formik.setFieldValue('remarks', inputValue);
                      setCharCount(70 - inputValue.length);
                    }
                  }}
                  placeholder='Remarks'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      formik.handleSubmit();
                    }
                  }}
                />
                <div className='text-end'>
                  <small className={charCount <= 10 ? 'text-danger' : ''}>
                    Remaining characters: {charCount}
                  </small>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-5">
            <Col md={12} className="d-flex justify-content-end">
              <Button variant="primary" type="submit" className="px-20 mx-w-100" disabled={loading}>
                {loading ? 'Please Wait...' : 'Submit'}
              </Button>
              <Button
                variant="secondary"
                type="button"
                className="ms-3 px-20 mx-w-100"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default AutoVatav;